import React, { useState } from "react";
import {
  TextInput,
  Checkbox,
  Button,
  Select,
  OptionsFilter,
  ComboboxItem,
} from "@mantine/core";
import jsPDF from "jspdf";
import "jspdf-autotable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import "./RemittancePage.css"; // Import custom CSS for styling
import { ApiService } from "./../api/api-service";
import { getAllInstructorDetails } from "../api/dynamodb-db-api";
import { useEffect } from "react";
import { useRef } from "react";

interface instructor {
  name: string;
  email: string;
  contact: string;
  sno: number;
  key: string;
  label: string;
  value: string;
}

const RemittancePage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    email: "",
    date1: new Date(), // First date
    date2: new Date(), //Trainer Invoice Date
    groupNumber: "",
    numStudents: "",
    numClasses: "",
    numLeaves: "",
    total: "",
    netAmountPaid: "",
    tdsChecked: false,
    leaveAvoidChecked: false,
    leavePaidChecked: false,
    leaveDeductionAmount: "",
    tdsAmount: "",
  });
  const [instructors, setInstructors] = useState<ComboboxItem[]>([]);
  const instructorsRef = useRef<instructor[]>([]);

  // const [instructorEmailSearch, setInstructorEmailSearch] = useState<any>();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange1 = (date: Date | null) => {
    if (date) {
      setFormData({ ...formData, date1: date });
    }
  };

  const handleDateChange2 = (date: Date | null) => {
    if (date) {
      setFormData({ ...formData, date2: date });
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.currentTarget;
    if (name === "leaveAvoidChecked" && checked) {
      setFormData({
        ...formData,
        leaveAvoidChecked: true,
        leavePaidChecked: false,
        leaveDeductionAmount: "",
      });
    } else if (name === "leavePaidChecked" && checked) {
      setFormData({
        ...formData,
        leavePaidChecked: true,
        leaveAvoidChecked: false,
      });
    } else {
      setFormData({ ...formData, [name]: checked });
    }
  };

  const handleCalculate = () => {
    const totalAmount = parseFloat(formData.total) || 0;
    const leaveDeduction = parseFloat(formData.leaveDeductionAmount) || 0;

    let netAmountPaid = totalAmount - leaveDeduction;
    const tdsAmount = formData.tdsChecked ? netAmountPaid * 0.1 : 0;

    netAmountPaid -= tdsAmount;

    const roundedNetAmount = Math.round(netAmountPaid * 100) / 100;

    setFormData({
      ...formData,
      netAmountPaid: roundedNetAmount.toFixed(2),
      tdsAmount: tdsAmount.toFixed(2),
    });
  };

  const generatePDF = (data: any) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Add logo centered and at the top
    const logoWidth = 35;
    const logoHeight = 10;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 20;

    doc.addImage(
      "https://ik.imagekit.io/2fljt9khj/logo%20L%20remove%20bg%20thinkcloudly.png?updatedAt=1719406869813",
      "PNG",
      logoX,
      logoY,
      logoWidth,
      logoHeight
    );

    // Set document font and size
    // doc.setFont('courier', 'normal');
    doc.setFontSize(12);

    // Title and details
    doc.setFont("Helvetica", "bold");
    // Set font to bold

    doc.text("Remittance Advice", pageWidth / 2, logoY + logoHeight + 10, {
      align: "center",
    });
    doc.text(`Issue Date: ${data.issueDate}`, 8, logoY + logoHeight + 20);
    doc.text(`Trainer Name: ${data.trainerName}`, 8, logoY + logoHeight + 30);
    doc.text(
      `Trainer Contact: ${data.trainerContact}`,
      8,
      logoY + logoHeight + 40
    );

    // Check if workDetails array exists and has content
    if (Array.isArray(data.workDetails) && data.workDetails.length > 0) {
      // AutoTable for work details
      (doc as any).autoTable({
        startY: logoY + logoHeight + 60,
        head: [
          [
            "Date",
            "Group Number",
            "No. of Students",
            "No. of Classes",
            "Total Leaves",
            "Amount",
          ],
        ],
        body: data.workDetails.map((item: any) => [
          item.date,
          item.groupNumber,
          item.numStudents.toString(),
          item.numClasses.toString(),
          item.totalLeaves.toString(),
          item.amount,
        ]),
        theme: "plain", // Use plain dark-colored grid
        styles: {
          textColor: [0, 0, 0], // Black text
          fillColor: [255, 255, 255], // White background
          lineColor: [0, 0, 0], // Black borders
          lineWidth: 0.2, // Border width
        },
      });
    } else {
      // Handle case where workDetails array is empty or undefined
      doc.text("No work details found.", 8, logoY + logoHeight + 60);
    }

    // Final payment details
    const finalY = (doc as any).lastAutoTable
      ? (doc as any).lastAutoTable.finalY + 10
      : logoY + logoHeight + 70;
    doc.setFontSize(12);
    doc.text("Final payment after Deductions:", 8, finalY);

    // AutoTable for final payment details
    (doc as any).autoTable({
      startY: finalY + 10,
      head: [
        [
          "No. of Extra Leaves",
          "Leave Amount Deducted",
          "TDS 10% Deducted",
          "Net Amount Paid",
        ],
      ],
      body: [
        [
          data.extraLeaves,
          data.leaveAmountDeducted,
          data.tdsDeducted,
          data.netAmountPaid,
        ],
      ],
      theme: "plain", // Use plain dark-colored grid
      styles: {
        textColor: [0, 0, 0], // Black text
        fillColor: [255, 255, 255], // White background
        lineColor: [0, 0, 0], // Black borders
        lineWidth: 0.2, // Border width
      },
    });

    // Additional contact and footer details
    const contactY = (doc as any).lastAutoTable
      ? (doc as any).lastAutoTable.finalY + 20
      : finalY + 40;
    doc.setFontSize(10);
    doc.text(
      "For any payment-related queries, please contact: mukesh.kumar@thinkcloudly.com",
      8,
      contactY
    );
    doc.text(
      "Thank you for your services. We appreciate your partnership.",
      8,
      contactY + 10
    );
    doc.text("By:", 8, contactY + 20);
    doc.text("Namrata Arora", 8, contactY + 30);
    doc.text(
      "This is a computerized document and does not require a physical signature.",
      8,
      contactY + 40
    );

    // Footer details
    const footerY = pageHeight - 20;
    doc.setFontSize(8);
    doc.text(
      "1111-21 Iceboat Terr, Toronto, Ontario M5V 4A9",
      pageWidth / 2,
      footerY,
      { align: "center" }
    );
    doc.text(
      "+1 (725) 710-9949   info@thinkcloudly.com   www.thinkcloudly.com",
      pageWidth / 2,
      footerY + 10,
      { align: "center" }
    );

    // Return base64 encoded PDF data
    const pdfDataUri = doc.output("datauristring");
    const base64Pdf = pdfDataUri.split(",")[1];
    return base64Pdf;
  };

  const generateRemittancePDF = () => {
    const {
      name,
      contact,
      email,
      date1,
      date2,
      groupNumber,
      numStudents,
      numClasses,
      numLeaves,
      total,
      netAmountPaid,
      tdsChecked,
      leaveAvoidChecked,
      leavePaidChecked,
      leaveDeductionAmount,
      tdsAmount,
    } = formData;

    const formattedDate1 = `${date1.getDate()} ${date1.toLocaleString(
      "default",
      { month: "long" }
    )} ${date1.getFullYear()}`;
    const formattedDate2 = `${date2.getDate()} ${date2.toLocaleString(
      "default",
      { month: "long" }
    )} ${date2.getFullYear()}`;

    const totalAmount = parseFloat(total) || 0;
    const leaveAmount = parseInt(numLeaves) || 0;
    const extraLeaves = leaveAmount > 3 ? leaveAmount - 3 : 0;
    const leaveDeducted = leaveAvoidChecked
      ? "NA"
      : leavePaidChecked
      ? `${leaveDeductionAmount}/-`
      : "NA";

    const data = {
      issueDate: formattedDate1, // Assuming formattedDate1 is for the first date
      secondDate: formattedDate2, // Adding the second date here
      trainerName: name,
      trainerContact: contact,
      workDetails: [
        {
          date: formattedDate2, // Assuming formattedDate1 is for the first date
          groupNumber: groupNumber,
          numStudents: parseInt(numStudents) || 0,
          numClasses: parseInt(numClasses) || 0,
          totalLeaves: leaveAmount,
          amount: `${totalAmount.toFixed(2)}/-`,
        },
      ],
      extraLeaves: extraLeaves > 0 ? extraLeaves.toString() : "NA",
      leaveAmountDeducted: leaveDeducted,
      tdsDeducted: tdsChecked ? `${tdsAmount}/-` : "NA",
      netAmountPaid: `${netAmountPaid}/-`,
    };

    console.log("Generated data for PDF:", data); // Log data to ensure it is correct
    return generatePDF(data);
  };

  const SendEmail = async () => {
    try {
      // Generate base64 PDF data
      const pdfBase64 = generateRemittancePDF();
      const { email } = formData;

      // Send email with PDF attachment
      const response = await ApiService.getInstance().post(
        `/email/remittance`,
        {
          ...formData,
          email: formData.email,
          pdfData: pdfBase64,
          name: formData.name,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Email sent successfully:", response.data);
      alert(`Email sent successfully to ${formData.email}`);
    } catch (error) {
      alert(`Error sending email to ${formData.email}`);
    }
  };

  // Add new code  so that email fetch in select dropdown correctly
  const addInstructor = (newInstructor: instructor) => {
    // Update Ref
    instructorsRef.current = [...instructorsRef.current, newInstructor];
  
    // Update State
    setInstructors((prev) => [
      ...prev,
      { label: newInstructor.email, value: newInstructor.email },
    ]);
  };
  useEffect(() => {
    setInstructors(
      instructorsRef.current.map(({ email }) => ({ label: email, value: email }))
    );
  }, [instructorsRef.current]);
    

  const handleChangeCommon = (key: string, value: string | null) => {
    const newFormData = {
      ...formData,
      [key]: value,
    };
    if (key === "email") {
      const instructorDetails = instructorsRef.current.find(
        (instructor) => instructor.email === value
      );
      newFormData["contact"] = instructorDetails?.contact as string;
      newFormData["name"] = instructorDetails?.name as string;
    }
    setFormData(newFormData);
  };

  // old code for Search Instructor in select dropdown

  // const handleInstructorSearch: OptionsFilter = ({ options, search }) => {
  //   if (!search) {
  //     return instructorsRef.current.map(({ email, name, contact }) => ({
  //       label: email,
  //       value: email,
  //     }));
  //   }
  
  //   const filteredOptions: ComboboxItem[] = [];
  
  //   instructorsRef.current.forEach(({ email, name, contact }) => {
  //     if (!email || !name || !contact) {
  //       console.warn("Incomplete instructor data:", { email, name, contact });
  //       return; // Skip this instructor
  //     }
  
  //     const searchItem = search?.toLowerCase() || ""; // Default empty string
  
  //     if (
  //       email.toLowerCase().includes(searchItem) ||
  //       name.toLowerCase().includes(searchItem) ||
  //       contact.toLowerCase().includes(searchItem)
  //     ) {
  //       filteredOptions.push({ label: email, value: email });
  //     }
  //   });
  
  //   return filteredOptions;
  // };
  


  // new code for Instructor Fetch so that updated Instructor Email is added
  const handleInstructorSearch: OptionsFilter = ({ options, search }) => {
    if (!search) {
      return instructors; // Use updated instructors state
    }
  
    return instructors.filter(({ label }) =>
      label.toLowerCase().includes(search.toLowerCase())
    );
  };
  
  useEffect(() => {
    getInstructors();
  }, []);

  const getInstructors = async () => {
    const response: instructor[] = await getAllInstructorDetails("instructors");
    instructorsRef.current = response;
    console.log(instructorsRef,"check instructorRef data")
    console.log(response,"check the response");
    
    const instructors: ComboboxItem[] = response.map(({ email }) => ({
      label: email,
      value: email,
    }));
    setInstructors(instructors);
  };

  return (
    <div className="remittance-page">
      <div className="section">
        <h3>VENDOR DETAILS:</h3>
        <div className="input-group">
          <div className="form-group">
            <label className="m_8fdc1311">Email</label>
            <Select
              id="email"
              name="email"
              data={instructors}
              value={formData.email}
              searchable
              required={true}
              onChange={(val) => handleChangeCommon("email", val)} // Ensure value is a string
              placeholder="Select email"
              nothingFoundMessage="Nothing found..."
              filter={handleInstructorSearch}
            />
          </div>
          <TextInput
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter name"
          />
          <TextInput
            label="Contact"
            name="contact"
            value={formData.contact}
            onChange={handleInputChange}
            placeholder="Enter contact number"
          />
        </div>
      </div>

      <div className="section">
        <h3>BATCH DETAILS:</h3>
        <div className="input-group">
          <div>
            <label>Issue Date:</label>
            <br />
            <DatePicker
              selected={formData.date1}
              onChange={handleDateChange1}
              dateFormat="dd MMMM yyyy"
              placeholderText="Select issue date"
              className="date-picker"
            />
          </div>
          <div>
            <label>Trainer Invoice Date:</label>
            <br />
            <DatePicker
              selected={formData.date2}
              onChange={handleDateChange2}
              dateFormat="dd MMMM yyyy"
              placeholderText="Select trainer invoice Date"
              className="date-picker"
            />
          </div>
          <TextInput
            label="Group Number"
            name="groupNumber"
            value={formData.groupNumber}
            onChange={handleInputChange}
            placeholder="Enter group number"
          />
          <TextInput
            label="Number of Students"
            name="numStudents"
            value={formData.numStudents}
            onChange={handleInputChange}
            placeholder="Enter number of students"
          />
          <TextInput
            label="Number of Classes"
            name="numClasses"
            value={formData.numClasses}
            onChange={handleInputChange}
            placeholder="Enter number of classes"
          />
          <TextInput
            label="Number of Leaves"
            name="numLeaves"
            value={formData.numLeaves}
            onChange={handleInputChange}
            placeholder="Enter number of Leaves"
          />
          <TextInput
            label="Total Amount"
            name="total"
            value={formData.total}
            onChange={handleInputChange}
            placeholder="Enter total amount"
          />
          <div className="total">
            <label>Net Amount Paid:</label>
            <span>{formData.netAmountPaid}</span>
          </div>
        </div>
      </div>

      <div className="section">
        <h3>Checkboxes:</h3>
        <div className="checkbox-group">
          <Checkbox
            label="TDS"
            name="tdsChecked"
            checked={formData.tdsChecked}
            onChange={handleCheckboxChange}
          />
          <Checkbox
            label="Leave Avoid"
            name="leaveAvoidChecked"
            checked={formData.leaveAvoidChecked}
            onChange={handleCheckboxChange}
          />
          {formData.leavePaidChecked && (
            <div className="manual-leave-deduction">
              <TextInput
                label="Leave Amount Deducted"
                name="leaveDeductionAmount"
                value={formData.leaveDeductionAmount}
                onChange={handleInputChange}
                placeholder="Enter leave deduction amount"
              />
            </div>
          )}
          <Checkbox
            label="Leave Paid"
            name="leavePaidChecked"
            checked={formData.leavePaidChecked}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="btns-vendor">
        <Button onClick={handleCalculate}>Calculate</Button>
        <Button onClick={SendEmail}>Send Mail</Button>
      </div>
    </div>
  );
};

export default RemittancePage;
